import React, { useState } from 'react';
import { X } from 'lucide-react';
import { NotificationRequest } from '../../../core/models/notification';
import { Modal } from '../../../core/components/Modal';
import { Button } from '../../../core/components/Button';
import { Input, Label, Textarea } from '../../../core/components/FormElements';
import './ExtendSubscriptionDialog.css';

interface Props {
    userId: string;
    onClose: () => void;
    onSuccess?: () => void;
    onSubmit: (data: { durationDays?: number; end?: string; request?: NotificationRequest }) => Promise<void>;
}

const ExtendSubscriptionDialog: React.FC<Props> = ({ userId, onClose, onSuccess, onSubmit }) => {
    const [durationDays, setDurationDays] = useState('');
    const [endDate, setEndDate] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        const duration = durationDays ? Number.parseInt(durationDays, 10) : undefined;
        const end = endDate || undefined;

        if (!duration && !end) {
            setError('Укажите длительность в днях или дату окончания');
            return;
        }

        const request = message ? { message } as NotificationRequest : undefined;

        try {
            setLoading(true);
            await onSubmit({ durationDays: duration, end, request });
            onSuccess?.();
            onClose();
        } catch (err: any) {
            setError(err?.message || 'Ошибка при продлении подписки');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal onClose={onClose} className="esd-dialog">
            <div className="esd-header">
                <h3>Продлить подписку пользователя</h3>
                <Button variant="ghost" size="sm" iconOnly icon={<X size={16} />} onClick={onClose} aria-label="Закрыть" />
            </div>

            <form onSubmit={handleSubmit} className="esd-form">
                <div className="esd-field">
                    <Label>Длительность (в днях)</Label>
                    <Input
                        type="number"
                        min={1}
                        value={durationDays}
                        onChange={(e) => setDurationDays(e.target.value)}
                        disabled={loading}
                    />
                </div>

                <div className="esd-field">
                    <Label>Или дата окончания</Label>
                    <Input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        disabled={loading}
                    />
                </div>

                <div className="esd-field">
                    <Label>Сообщение уведомления (опционально)</Label>
                    <Textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={3}
                        disabled={loading}
                        placeholder="Текст уведомления, если нужно отправить сообщение"
                    />
                </div>

                {error && <div className="esd-error">{error}</div>}

                <div className="esd-actions">
                    <Button variant="secondary" type="button" onClick={onClose} disabled={loading}>Отмена</Button>
                    <Button variant="primary" type="submit" loading={loading}>Продлить</Button>
                </div>
            </form>
        </Modal>
    );
};

export default ExtendSubscriptionDialog;
